<template>
  <PageHeader title="AI Prompts" subtitle="Manage AI prompt templates for report generation" />

  <div class="prompts-container">
    <div class="prompts-layout">
      <!-- Tabs -->
      <div class="tabs">
        <button
          v-for="template in templates"
          :key="template.key"
          :class="['tab', { active: activeTab === template.key }]"
          @click="selectTab(template.key)"
        >
          {{ template.name }}
        </button>
      </div>

      <!-- Editor Area -->
      <div v-if="currentTemplate" class="editor-area">
        <div class="editor-main">
          <PromptEditor :template="currentTemplate" @save="handleSave" />
        </div>

        <div class="editor-sidebar">
          <PromptVariables :variables="variables" />

          <div class="sidebar-actions bg-white br-10px">
            <AppButton variant="outlined" :disabled="!currentTemplate.has_versions" @click="showVersionHistory = true">
              <AppIcon type="history" size="18" class="mr-5px" /> Version History
            </AppButton>
            <AppButton variant="danger" @click="handleReset">
              <AppIcon type="reset" size="18" class="mr-5px" /> Reset to Default
            </AppButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Version History Modal -->
    <PromptVersionHistory
      v-if="showVersionHistory"
      :versions="versions"
      @close="showVersionHistory = false"
      @restore="handleRestore"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { AppButton, AppIcon } from "@/components/ui";
import PageHeader from "@/components/PageHeader.vue";
import PromptEditor from "./components/PromptEditor.vue";
import PromptVariables from "./components/PromptVariables.vue";
import PromptVersionHistory from "./components/PromptVersionHistory.vue";
import { usePromptTemplates } from "@/composables/usePromptTemplates";

const {
  templates,
  currentTemplate,
  variables,
  versions,
  fetchAll,
  fetchOne,
  save,
  fetchVersions,
  restoreVersion,
  resetToDefault,
} = usePromptTemplates();

const activeTab = ref(null);
const showVersionHistory = ref(false);

onMounted(async () => {
  await fetchAll();
  if (templates.value.length > 0) {
    selectTab(templates.value[0].key);
  }
});

const selectTab = async (key) => {
  activeTab.value = key;
  await fetchOne(key);
};

const handleSave = async ({ systemPrompt, userPrompt }) => {
  await save(activeTab.value, systemPrompt, userPrompt);
};

const handleReset = async () => {
  if (
    !confirm(
      "Are you sure you want to reset this prompt to the default? Your current changes will be saved as a version."
    )
  ) {
    return;
  }
  await resetToDefault(activeTab.value);
};

const handleRestore = async (version) => {
  if (
    !confirm(`Are you sure you want to restore version ${version}? Your current changes will be saved as a version.`)
  ) {
    return;
  }
  await restoreVersion(activeTab.value, version);
  showVersionHistory.value = false;
};

// Load versions when modal opens
watch(showVersionHistory, async (show) => {
  if (show && activeTab.value) {
    await fetchVersions(activeTab.value);
  }
});
</script>

<style lang="scss" scoped>
.prompts-container {
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;
}

.prompts-layout {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.tabs {
  display: flex;
  gap: 8px;
  border-bottom: 2px solid $color-border;
  overflow-x: auto;
}

.tab {
  padding: 12px 20px;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  font-size: 15px;
  color: $color-text-muted;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  white-space: nowrap;
  transition:
    color 150ms ease-out,
    border-color 150ms ease-out;

  &:hover {
    color: $color-ink;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: -2px;
  }

  &.active {
    color: $color-primary;
    border-bottom-color: $color-primary;
    font-weight: 600;
  }
}

.editor-area {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
}

.editor-main {
  min-width: 0;
}

.editor-sidebar {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}

@media (max-width: 1024px) {
  .editor-area {
    grid-template-columns: 1fr;
  }

  .editor-sidebar {
    order: -1;
  }
}
</style>
